import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts';

interface UseTokensReturn {
  tokensBalance: number;
  tokensTotal: number;
  hasEnoughTokens: (minTokens?: number) => boolean;
  checkTokensBeforeAction: (minTokens?: number) => boolean;
  showInsufficientTokensModal: () => void;
  isModalOpen: boolean;
  closeModal: () => void;
  isBuyTokensModalOpen: boolean;
  openBuyTokensModal: () => void;
  closeBuyTokensModal: () => void;
}

/**
 * Hook para gerenciar verificação de tokens
 * Verifica se o usuário possui tokens suficientes antes de executar ações que usam IA
 */
export const useTokens = (): UseTokensReturn => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBuyTokensModalOpen, setIsBuyTokensModalOpen] = useState(false);
  const previousBalanceRef = useRef<number | null>(null);
  const lowBalanceWarnedRef = useRef(false);

  const tokensBalance = user?.ds_tokens_balance ?? 0;
  const tokensTotal = user?.ds_tokens_total ?? 0;
  const LOW_BALANCE_THRESHOLD = 10;
  const CRITICAL_BALANCE_THRESHOLD = 5;

  // Avisar quando o saldo estiver baixo
  useEffect(() => {
    // Só avisar se o saldo mudou e está abaixo do threshold
    if (
      previousBalanceRef.current !== null &&
      previousBalanceRef.current !== tokensBalance
    ) {
      if (tokensBalance === 0) {
        toast.error(t('tokens.noTokensAvailable'), {
          duration: 5000,
          icon: '⚠️',
        });
        lowBalanceWarnedRef.current = false;
      } else if (tokensBalance <= CRITICAL_BALANCE_THRESHOLD && !lowBalanceWarnedRef.current) {
        toast.error(t('tokens.criticalBalance'), {
          duration: 5000,
          icon: '🔴',
        });
        lowBalanceWarnedRef.current = true;
      } else if (
        tokensBalance <= LOW_BALANCE_THRESHOLD &&
        tokensBalance > CRITICAL_BALANCE_THRESHOLD &&
        !lowBalanceWarnedRef.current
      ) {
        toast(t('tokens.lowBalanceWarning'), {
          duration: 4000,
          icon: '⚠️',
          style: {
            background: '#fbbf24',
            color: '#fff',
          },
        });
        lowBalanceWarnedRef.current = true;
      } else if (tokensBalance > LOW_BALANCE_THRESHOLD) {
        lowBalanceWarnedRef.current = false;
      }
    }

    previousBalanceRef.current = tokensBalance;
  }, [tokensBalance, t]);

  /**
   * Verifica se há tokens suficientes
   * @param minTokens Número mínimo de tokens necessário (padrão: 1)
   * @returns true se houver tokens suficientes, false caso contrário
   */
  const hasEnoughTokens = useCallback(
    (minTokens: number = 1): boolean => {
      return tokensBalance >= minTokens;
    },
    [tokensBalance]
  );

  /**
   * Verifica tokens antes de executar uma ação
   * Se não houver tokens suficientes, abre o modal
   * @param minTokens Número mínimo de tokens necessário (padrão: 1)
   * @returns true se pode prosseguir, false se bloqueado
   */
  const checkTokensBeforeAction = useCallback(
    (minTokens: number = 1): boolean => {
      if (!hasEnoughTokens(minTokens)) {
        setIsModalOpen(true);
        // Toast adicional para reforçar o aviso
        toast.error(t('tokens.actionBlocked'), {
          duration: 3000,
          icon: '🚫',
        });
        return false;
      }
      return true;
    },
    [hasEnoughTokens, t]
  );

  const showInsufficientTokensModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const openBuyTokensModal = useCallback(() => {
    setIsBuyTokensModalOpen(true);
  }, []);

  const closeBuyTokensModal = useCallback(() => {
    setIsBuyTokensModalOpen(false);
  }, []);

  return {
    tokensBalance,
    tokensTotal,
    hasEnoughTokens,
    checkTokensBeforeAction,
    showInsufficientTokensModal,
    isModalOpen,
    closeModal,
    isBuyTokensModalOpen,
    openBuyTokensModal,
    closeBuyTokensModal,
  };
};
